<template>
    <div>
        <v-card v-if="stationLocation" class="ma-3 pa-5">
            <v-row class="pa-3" justify="space-between">
                <h3>{{ stationLocation.alias }}</h3>
                <v-spacer/>
                <v-tooltip bottom v-if="['super_admin'].includes(user.role)">
                    <template v-slot:activator="{ on }">
                        <v-btn class="mr-2" color="primary" @click="handleRecalculateNo3" v-on="on" :loading="no3Loading">
                            NO<sub>3</sub>
                        </v-btn>
                    </template>
                    <span>Recalculate NO<sub>3</sub></span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="mr-2" color="primary" @click="openInMaps" v-on="on">
                            <v-icon>mdi-earth</v-icon>
                        </v-btn>
                    </template>
                    <span>Open in google maps</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="mr-2" color="primary" @click="graphLocation" v-on="on">
                            <v-icon>mdi-chart-bell-curve-cumulative</v-icon>
                        </v-btn>
                    </template>
                    <span>Graph</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="mr-2" @click="handleDuplicateLocationClick" color="primary" v-on="on">
                            <v-icon>mdi-content-duplicate</v-icon>
                        </v-btn>
                    </template>
                    <span>Duplicate</span>
                </v-tooltip>
                <v-tooltip v-if="user.role === 'super_admin'" bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn class="mr-2" color="primary" @click="removeLocation" v-on="on">
                            <v-icon>mdi-trash-can-outline</v-icon>
                        </v-btn>
                    </template>
                    <span>Remove</span>
                </v-tooltip>
            </v-row>
            <v-divider class="my-3"/>
            <v-text-field :value="stationLocation.alias" label="Alias"/>
            <v-text-field :value="stationLocation.latitude" disabled label="Latitude"/>
            <v-text-field :value="stationLocation.longitude" disabled label="Longitude"/>
            <v-text-field :value="stationLocation.country_code" disabled label="Country"/>
            <v-text-field :value="stationLocation.region" disabled label="Region"/>
            <v-select v-model="stationLocation.culture_id"
                      :items="cultures"
                      item-text="locale.en_culture"
                      item-value="id"
                      label="Culture"/>
            <v-select v-model="stationLocation.tillage_method_id"
                      :items="tillageMethods"
                      item-text="name"
                      item-value="id"
                      label="Tillage method"/>
            <v-select v-model="stationLocation.location_company_id"
                      :items="companies"
                      item-text="company"
                      item-value="id"
                      label="Company"/>
            <v-select
                v-model="stationLocation.field_id"
                :clearable="true"
                :items="stationLocation.field_list"
                item-text="name"
                item-value="id"
                label="Field"
                @change="handleFieldChange($event)"
            />
            <v-select
                v-model="stationLocation.soil_type_id"
                :clearable="false"
                :items="soilTypes"
                item-text="name"
                item-value="id"
                label="Soil Type"
            />
            <v-select
                v-model="stationLocation.sector_id"
                :clearable="false"
                :items="sectors"
                item-text="name"
                item-value="id"
                label="Sector"
            />
            <v-menu
                v-model="show_start_date_menu"
                :close-on-content-click="false"
                min-width="auto"
                offset-y
                transition="scale-transition"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        :value="formatDate(stationLocation.start_date)"
                        append-icon="mdi-calendar"
                        class="min-width-115"
                        label="Start Date"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="stationLocation.start_date"
                    first-day-of-week="1"
                    @input="show_start_date_menu = false"
                ></v-date-picker>
            </v-menu>
            <v-menu
                v-model="show_end_date_menu"
                :close-on-content-click="false"
                min-width="auto"
                offset-y
                transition="scale-transition"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        :value="formatDate(stationLocation.end_date)"
                        append-icon="mdi-calendar"
                        class="min-width-115"
                        label="End Date"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="stationLocation.end_date"
                    first-day-of-week="1"
                    @input="show_end_date_menu = false"
                ></v-date-picker>
            </v-menu>
            <v-select
                v-model="stationLocation.season_id"
                :items="seasons"
                item-text="title"
                item-value="id"
                label="Season"
            />
            <v-autocomplete
                v-model="stationLocation.users"
                :items="noAdminUsers"
                chips
                item-text="name_email"
                item-value="id"
                label="Users"
                multiple
                return-object

            >
            </v-autocomplete>
            <v-btn class="mt-3" color="primary" @click="updateLocation">Save</v-btn>
        </v-card>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import moment from 'moment/moment';

export default {
    name: 'StationViewInformation',
    data() {
        return {
            show_start_date_menu: false,
            show_end_date_menu: false,
            no3Loading: false,
        };
    },
    computed: {
        ...mapState('stationLocation', ['stationLocation']),
        ...mapState('culture', ['cultures']),
        ...mapState('probe', ['tillageMethods', 'seasons', 'companies', 'sectors']),
        ...mapState('soil', ['soilTypes']),
        ...mapState('app', ['users']),
        ...mapState('auth', ['user']),
        ...mapGetters('app', ['noAdminUsers']),
    },
    async beforeMount() {
        if (!this.cultures?.length) await this.fetchCulture();
        if (!this.tillageMethods?.length) await this.fetchTillageMethods();
        if (!this.sectors?.length) await this.fetchSectors();
        if (!this.soilTypes?.length) await this.fetchSoilTypesList();
        if (!this.seasons?.length) await this.fetchSeasons();
        if (!this.companies?.length) await this.fetchLocationCompany();
    },
    methods: {
        ...mapActions('culture', ['fetchCulture']),
        ...mapActions('probe', ['fetchTillageMethods', 'fetchSeasons', 'duplicateLocation', 'removeProbeLocation', 'recalculateNo3', 'updateProbeLocation', 'fetchLocationCompany', 'fetchSectors']),
        ...mapActions('soil', ['fetchSoilTypesList']),
        async handleRecalculateNo3() {
            this.no3Loading = true;
            await this.recalculateNo3({
                probeId: this.stationLocation.station_id,
                locationId: this.stationLocation.id,
            });
            this.no3Loading = false;
        },
        updateLocation() {
            if (!this.stationLocation?.alias || !this.stationLocation?.culture_id || !this.stationLocation?.location_company_id || !this.stationLocation?.season_id) {
                alert('Please fill name, culture, company and season field!');
                return;
            }
            this.updateProbeLocation({probeId: this.stationLocation.station_id, location: this.stationLocation});
        },
        async removeLocation() {
            if (confirm('Would you like to remove this location?')) {
                await this.$router.push({name: 'station-view', params: {id: this.stationLocation.station_id}});
                await this.removeProbeLocation({
                    probeId: this.stationLocation.station_id,
                    locationId: this.stationLocation.id,
                });
            }
        },
        openInMaps() {
            let url = 'https://maps.google.com/?t=k&q=' + this.stationLocation.latitude + ',' + this.stationLocation.longitude;
            window.open(url, '_blank');
        },
        graphLocation() {
            let url = new URL(`${process.env.VUE_APP_APP_URL}/graph/${this.stationLocation.id}`);
            window.open(url, '_blank');
        },
        async handleDuplicateLocationClick() {
            if (!this.stationLocation.latitude || !this.stationLocation.longitude) {
                alert('Latitude and longitude required');
                return;
            }
            const duplicateLocation = await this.duplicateLocation({
                activeProbeId: this.stationLocation.station_id,
                locationId: this.stationLocation.id,
            });
            if (duplicateLocation) {
                await this.$router.push({
                    name: 'station-location',
                    params: {probeId: duplicateLocation.station_id, locationId: duplicateLocation.id},
                });
            }
        },
        handleFieldChange(fieldId) {
            const selectedFieldId = fieldId ? parseInt(fieldId) : -1;
            const user = this.stationLocation.field_list.filter(f => f.id === selectedFieldId)[0]?.created_by_user;
            const selectedUserId = user ? user.id : -1;
            let users = this.stationLocation.users?.filter(u => !u.selected && u.id !== selectedUserId) ?? []; // filter out currently selected user
            if (user) {
                user.selected = true;
                this.stationLocation.users = [...users, user];
            } else {
                this.stationLocation.users = [...users];
            }
        },
        formatDate(date) {
            if (date) {
                return moment(date).format('DD.MM.YYYY');
            }
            return '-';
        },
    },
};
</script>

<style scoped>

</style>